import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function FooterSettingsManager({ authToken }) {
  const [settings, setSettings] = useState({
    copyright_text: "Sport und Freizeit in Wilmersdorf e.V.",
    version: "V15",
    show_year: true,
    show_copyright: true,
    show_version: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${authToken}` } }),
    [authToken]
  );

  // Einstellungen laden
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/footer-settings`, authHeaders());
        setSettings(res.data);
      } catch (error) {
        console.error("Fehler beim Laden der Footer-Einstellungen:", error);
      }
      setLoading(false);
    };
    loadSettings();
  }, [authHeaders]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/api/footer-settings`, settings, authHeaders());
      setSettings(res.data);
      setMessage({ type: "success", text: "Footer-Einstellungen gespeichert!" });
      setHasChanges(false);
      // Event auslösen, damit der Footer aktualisiert wird
      window.dispatchEvent(new CustomEvent('footer-settings-updated', { detail: res.data }));
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      setMessage({ type: "error", text: "Fehler beim Speichern der Footer-Einstellungen" });
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Möchten Sie die Footer-Einstellungen auf die Standardwerte zurücksetzen?")) {
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/api/footer-settings/reset`, {}, authHeaders());
      setSettings(res.data);
      setMessage({ type: "success", text: "Footer-Einstellungen zurückgesetzt!" });
      setHasChanges(false);
      window.dispatchEvent(new CustomEvent('footer-settings-updated', { detail: res.data }));
    } catch (error) {
      console.error("Fehler beim Zurücksetzen:", error);
      setMessage({ type: "error", text: "Fehler beim Zurücksetzen" });
    }
    setSaving(false);
  };

  // Vorschau generieren
  const getPreview = () => {
    const parts = [];
    if (settings.show_copyright) {
      if (settings.show_year) {
        parts.push(`© ${new Date().getFullYear()} ${settings.copyright_text}`);
      } else {
        parts.push(`© ${settings.copyright_text}`);
      }
    }
    if (settings.show_version && settings.version) {
      parts.push(`Version: ${settings.version}`);
    }
    return parts.join(" - ");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="footer-settings-manager">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-r from-slate-100 to-white p-4 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-slate-900" style={{ fontSize: 'var(--font-h3-size)', fontWeight: 'var(--font-h3-weight)' }}>
              Footer-Einstellungen
            </h3>
            <p className="mt-1 text-slate-500" style={{ fontSize: 'var(--font-body-size)' }}>
              Konfigurieren Sie den Copyright-Text und die Versionsnummer am unteren Seitenrand.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              disabled={saving}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
            >
              Standard
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="rounded-lg bg-slate-800 px-4 py-2 text-white hover:bg-slate-700 disabled:opacity-50"
              style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-body-weight)' }}
            >
              {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </div>

        {/* Status-Meldung */}
        {message && (
          <div
            className={`mt-3 rounded-lg p-3 ${
              message.type === "success"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-rose-100 text-rose-700"
            }`}
            style={{ fontSize: 'var(--font-body-size)' }}
          >
            {message.text}
          </div>
        )}
      </div>

      {/* Einstellungen */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="grid gap-6 sm:grid-cols-2">
          {/* Copyright-Text */}
          <div>
            <label className="mb-2 block text-slate-700" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
              Copyright-Text
            </label>
            <input
              type="text"
              value={settings.copyright_text}
              onChange={(e) => handleChange("copyright_text", e.target.value)}
              placeholder="z.B. Mein Verein e.V."
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 focus:border-slate-500 focus:outline-none"
              style={{ fontSize: 'var(--font-body-size)' }}
            />
            <p className="mt-1 text-slate-400" style={{ fontSize: 'var(--font-small-size)' }}>
              Name des Vereins oder der Organisation
            </p>
          </div>

          {/* Versionsnummer */}
          <div>
            <label className="mb-2 block text-slate-700" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
              Versionsnummer
            </label>
            <input
              type="text"
              value={settings.version}
              onChange={(e) => handleChange("version", e.target.value)}
              placeholder="z.B. V15"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 focus:border-slate-500 focus:outline-none"
              style={{ fontSize: 'var(--font-body-size)' }}
            />
            <p className="mt-1 text-slate-400" style={{ fontSize: 'var(--font-small-size)' }}>
              Aktuelle Version der Anwendung (z.B. V15, V16, ...)
            </p>
          </div>
        </div>

        {/* Anzeigeoptionen */}
        <div className="mt-6 pt-4 border-t border-slate-100">
          <h4 className="mb-3 text-slate-700" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
            Anzeigeoptionen
          </h4>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.show_copyright}
                onChange={(e) => handleChange("show_copyright", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
              />
              <span className="text-slate-700" style={{ fontSize: 'var(--font-body-size)' }}>
                Copyright anzeigen
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.show_year}
                onChange={(e) => handleChange("show_year", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
              />
              <span className="text-slate-700" style={{ fontSize: 'var(--font-body-size)' }}>
                Jahr anzeigen
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.show_version}
                onChange={(e) => handleChange("show_version", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
              />
              <span className="text-slate-700" style={{ fontSize: 'var(--font-body-size)' }}>
                Version anzeigen
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Vorschau */}
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <h4 className="mb-3 text-slate-700" style={{ fontSize: 'var(--font-body-size)', fontWeight: 'var(--font-label-weight)' }}>
          Vorschau
        </h4>
        <div className="rounded-lg bg-slate-50 border-t border-slate-200 py-4">
          <p 
            className="text-center text-slate-500"
            style={{ 
              fontSize: 'var(--font-small-size)', 
              fontWeight: 'var(--font-body-weight)',
              fontFamily: 'var(--font-primary)'
            }}
          >
            {getPreview() || "(Keine Anzeige konfiguriert)"}
          </p>
        </div>
      </div>

      {/* Info-Box */}
      <div className="rounded-lg bg-slate-100 p-3 text-slate-600" style={{ fontSize: 'var(--font-body-size)' }}>
        <strong>Tipp:</strong> Nach einem neuen Fork können Sie hier die Versionsnummer aktualisieren (z.B. V15 → V16).
      </div>
    </div>
  );
}
